import React, { useState, useEffect } from "react";
import { Dropdown, DateRangePicker, Radio, Button } from "../../shared";
import {
  format,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfYear,
  endOfYear,
} from "date-fns";

/**
 * DateFilterDropdown component
 * A dropdown that allows users to select date filters including:
 * - Predefined options (Today, This Week, This Month, This Year, etc.)
 * - Custom date range
 * - Single date selection
 *
 * @param {Object} props - Component props
 * @param {string} props.align - Dropdown alignment (left/right)
 * @param {React.ReactNode} props.trigger - Trigger element for the dropdown
 * @param {Function} props.onChange - Function called when date selection changes
 * @param {Object} props.value - Currently selected date value
 * @returns {JSX.Element} - Rendered component
 */
const DateFilterDropdown = ({
  align = "left",
  trigger,
  onChange,
  value,
  className,
  ...props
}) => {
  const [filterType, setFilterType] = useState("thisWeek");
  const [dateRange, setDateRange] = useState([null, null]);
  const [singleDate, setSingleDate] = useState(null);

  // Sync internal state with value prop
  useEffect(() => {
    if (value && value.type) {
      setFilterType(value.type);
    }
  }, [value]);

  // Handle predefined date selections
  const handlePredefinedSelection = (type) => {
    setFilterType(type);
    const today = new Date();

    let start, end;

    switch (type) {
      case "today":
        start = startOfDay(today);
        end = endOfDay(today);
        break;
      case "thisWeek":
        start = startOfWeek(today);
        end = endOfWeek(today);
        break;
      case "lastWeek": {
        const lastWeek = subWeeks(today, 1);
        start = startOfWeek(lastWeek);
        end = endOfWeek(lastWeek);
        break;
      }
      case "thisMonth":
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case "thisYear":
        start = startOfYear(today);
        end = endOfYear(today);
        break;
      case "lastMonth": {
        const lastMonth = subMonths(today, 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        break;
      }
      case "custom":
        // Don't set start/end here - wait for the date picker selection
        return;
      default: // all
        start = null;
        end = null;
    }

    if (onChange) {
      onChange({ type, start, end });
    }
  };

  // Handle custom date range selection
  const handleDateRangeChange = (range) => {
    const [start, end] = range;
    setDateRange(range);

    if (start && end && onChange) {
      onChange({
        type: "customRange",
        start: startOfDay(start),
        end: endOfDay(end),
      });
    }
  };

  // Handle single date selection
  const handleSingleDateChange = (date) => {
    setSingleDate(date);

    if (date && onChange) {
      onChange({
        type: "singleDate",
        start: startOfDay(date),
        end: endOfDay(date),
      });
    }
  };

  // Apply button handler for custom selections
  const handleApply = () => {
    if (filterType === "customRange" && dateRange[0] && dateRange[1]) {
      onChange({
        type: "customRange",
        start: startOfDay(dateRange[0]),
        end: endOfDay(dateRange[1]),
      });
    } else if (filterType === "singleDate" && singleDate) {
      onChange({
        type: "singleDate",
        start: startOfDay(singleDate),
        end: endOfDay(singleDate),
      });
    }
  };

  // Format the displayed date range for the trigger
  const formatDisplayDate = () => {
    if (!value || !value.type) return "All Time";

    if (value.type === "all") return "All Time";
    if (value.type === "today") return "Today";
    if (value.type === "thisWeek") return "This Week";
    if (value.type === "lastWeek") return "Last Week";
    if (value.type === "thisMonth") return "This Month";
    if (value.type === "thisYear") return "This Year";
    if (value.type === "lastMonth") return "Last Month";

    if (value.start && value.end) {
      if (
        value.type === "singleDate" ||
        format(value.start, "yyyy-MM-dd") === format(value.end, "yyyy-MM-dd")
      ) {
        return format(value.start, "MMM dd, yyyy");
      }
      return `${format(value.start, "MMM dd, yyyy")} - ${format(
        value.end,
        "MMM dd, yyyy"
      )}`;
    }

    return "Select Date";
  };

  // Determine if we need to show the expanded view
  const isExpanded =
    filterType === "customRange" || filterType === "singleDate";

  // Custom dropdown content
  const dropdownContent = (close) => (
    <div
      className={`p-4 space-y-4 ${
        isExpanded ? "min-w-[275px]" : "min-w-[100px]"
      }`}
    >
      <div className="">
        <div className="">
          <Radio
            id="today"
            checked={filterType === "today"}
            onChange={() => handlePredefinedSelection("today")}
            label="Today"
            size="xs"
          />
          <Radio
            id="thisWeek"
            checked={filterType === "thisWeek"}
            onChange={() => handlePredefinedSelection("thisWeek")}
            label="This Week"
            size="xs"
          />
          <Radio
            id="lastWeek"
            checked={filterType === "lastWeek"}
            onChange={() => handlePredefinedSelection("lastWeek")}
            label="Last Week"
            size="xs"
          />
          <Radio
            id="thisMonth"
            checked={filterType === "thisMonth"}
            onChange={() => handlePredefinedSelection("thisMonth")}
            label="This Month"
            size="xs"
          />
          <Radio
            id="thisYear"
            checked={filterType === "thisYear"}
            onChange={() => handlePredefinedSelection("thisYear")}
            label="This Year"
            size="xs"
          />
          <Radio
            id="lastMonth"
            checked={filterType === "lastMonth"}
            onChange={() => handlePredefinedSelection("lastMonth")}
            label="Last Month"
            size="xs"
          />
          <Radio
            id="customRange"
            checked={filterType === "customRange"}
            onChange={() => setFilterType("customRange")}
            label="Custom Range"
            size="xs"
          />
          <Radio
            id="singleDate"
            checked={filterType === "singleDate"}
            onChange={() => setFilterType("singleDate")}
            label="Single Date"
            size="xs"
          />
          <Radio
            id="all"
            checked={filterType === "all"}
            onChange={() => handlePredefinedSelection("all")}
            label="All Time"
            size="xs"
          />
        </div>
      </div>

      {filterType === "customRange" && (
        <div className="space-y-2">
          <h3 className="font-medium text-sm text-gray-700">
            Custom Date Range
          </h3>
          <DateRangePicker
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            onChange={handleDateRangeChange}
            selectsRange={true}
            className="w-full"
            monthsShown={1}
            popperPlacement="bottom"
          />
        </div>
      )}

      {filterType === "singleDate" && (
        <div className="space-y-2">
          <h3 className="font-medium text-sm text-gray-700">Select Date</h3>
          <DateRangePicker
            startDate={singleDate}
            endDate={null}
            onChange={([date]) => handleSingleDateChange(date)}
            selectsRange={false}
            className="w-full"
          />
        </div>
      )}

      {(filterType === "customRange" || filterType === "singleDate") && (
        <div className="flex justify-end space-x-2 pt-2">
          <Button size="xs" type="light" onClick={close}>
            Cancel
          </Button>
          <Button
            size="xs"
            onClick={() => {
              handleApply();
              close();
            }}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      align={align}
      trigger={
        trigger || (
          <div
            className={`flex items-center cursor-pointer font-semibold ${
              className || ""
            }`}
          >
            <span>{formatDisplayDate()}</span>
          </div>
        )
      }
      data={dropdownContent}
      closeOnClick
      widthClasses={isExpanded ? "w-auto" : "w-56"}
      {...props}
    />
  );
};

export default DateFilterDropdown;
